import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import { SHAREBOX_RESULTS, SHAREBOX_TRIGGERS } from "models";
import { useShareboxFilterHandlers, useShareboxFilters } from "modules/ShareboxDashboard/components/ShareboxDashboardTable/hooks";
import "modules/ShareboxDashboard/components/ShareboxDashboardTable/ShareboxDashboardTable.scss";
import { useShareboxLogsQuery } from "modules/ShareboxDashboard/hooks";
import { PAGE_SIZE, convertSnakeCaseToWords } from "utils/common";
import { formatDate, toDate } from "utils/dateHelperFunctions";

import type { ShareboxLog } from "models";

const columnHelper = createColumnHelper<ShareboxLog>();

const SHAREBOX_TRIGGER_MAP: Record<SHAREBOX_TRIGGERS, { label: string; defaultText: string }> = {
  [SHAREBOX_TRIGGERS.EmployeeDropKey]: { label: "v8_employee_drop_key", defaultText: "Employee Drop Key" },
  [SHAREBOX_TRIGGERS.DoorOpened]: { label: "v8_door_opened", defaultText: "Drop Opened" },
  [SHAREBOX_TRIGGERS.EmployeePickUpKey]: { label: "v8_employee_pick_uo_key", defaultText: "Employee Pick Up Key" },
  [SHAREBOX_TRIGGERS.ReceiverDropKey]: { label: "v8_receiver_drop_key", defaultText: "Receiver Drop Key" },
  [SHAREBOX_TRIGGERS.ReceiverPickUpKey]: { label: "v8_receiver_pick_uo_key", defaultText: "Receiver Pick Up Key" }
};

const SHAREBOX_RESULT_MAP: Record<SHAREBOX_RESULTS, { label: string; defaultText: string }> = {
  [SHAREBOX_RESULTS.Completed]: { label: "v8_completed", defaultText: "Completed" },
  [SHAREBOX_RESULTS.Error]: { label: "v8_error", defaultText: "Error" },
  [SHAREBOX_RESULTS.RegNumberDoNotMatch]: { label: "v8_reg_number_do_not_match", defaultText: "Reg Number Do Not Match" }
};

export const ShareboxDashboardTable = () => {
  const { t } = useTranslation();

  const { data: page } = useShareboxFilters();
  const { shareboxLogs, nbPages, isFetching: isFetchingShareboxLogs } = useShareboxLogsQuery(page);
  const handlePaginationChange = useShareboxFilterHandlers();

  const tablePagination = nbPages > 1 ? { pageIndex: page - 1, pageSize: PAGE_SIZE.PageSize100 } : undefined;

  const columns = [
    columnHelper.accessor(row => row, {
      id: "trigger_id",
      header: t("v8_trigger_id", "Trigger ID"),
      cell: ({ row: { original } }) => {
        const shareboxTrigger = {
          label: SHAREBOX_TRIGGER_MAP[original.trigger_id].label || "",
          defaultText: SHAREBOX_TRIGGER_MAP[original.trigger_id].defaultText || ""
        };
        return <span>{t(shareboxTrigger.label, shareboxTrigger.defaultText)}</span>;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "result_id",
      header: t("v8_result_id", "Result ID"),
      cell: ({ row: { original } }) => {
        const shareboxResult = { label: SHAREBOX_RESULT_MAP[original.result_id].label || "", defaultText: SHAREBOX_RESULT_MAP[original.result_id].defaultText || "" };
        const resultId = t(shareboxResult.label, shareboxResult.defaultText);
        return <Popup content={resultId} trigger={<div className="sharebox-table-ellipsis">{resultId}</div>} />;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "reason",
      header: t("v8_reason", "Reason"),
      cell: ({ row: { original } }) => {
        if (!original.reason) return null;

        const convertedReason = convertSnakeCaseToWords(original.reason);
        return (
          <Popup
            content={<div className="capitalize-text">{convertedReason}</div>}
            trigger={<div className="sharebox-table-ellipsis capitalize-text">{convertedReason}</div>}
          />
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "locker_no",
      header: `${t("v8_locker_no", "Locker No")}.`,
      cell: ({ row: { original } }) => original.locker_no
    }),
    columnHelper.accessor(row => row, {
      id: "reservation_id",
      header: t("v8_reservation_id", "Reservation ID"),
      cell: ({ row: { original } }) => original.reservation_id
    }),
    columnHelper.accessor(row => row, {
      id: "subscription_id",
      header: t("v8_subscription_id", "Subscription ID "),
      cell: ({ row: { original } }) => original.subscription_id
    }),
    columnHelper.accessor(row => row, {
      id: "subscription_name",
      header: t("v8_subscription_name", "Subscription Name"),
      cell: ({ row: { original } }) => {
        const convertedSubscriptionName = convertSnakeCaseToWords(original.subscription_name);
        return (
          <Popup
            content={<div className="capitalize-text">{convertedSubscriptionName}</div>}
            trigger={<div className="sharebox-table-ellipsis capitalize-text">{convertedSubscriptionName}</div>}
          />
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "event_type",
      header: t("v8_event_type", "Event Type"),
      cell: ({ row: { original } }) => {
        const convertedEventType = convertSnakeCaseToWords(original.event_type);
        return (
          <Popup
            content={<div className="capitalize-text">{convertedEventType}</div>}
            trigger={<div className="sharebox-table-ellipsis capitalize-text">{convertedEventType}</div>}
          />
        );
      }
    }),
    columnHelper.accessor(row => row, {
      id: "event_timestamp",
      header: t("v8_event_timestamp", "Event Timestamp"),
      cell: ({ row: { original } }) => {
        const eventTimestamp = formatDate(toDate(original.event_timestamp), DATE_FORMATS_FNS.yearMonthDatehoursMinutes);
        return <Popup content={eventTimestamp} trigger={<div className="sharebox-table-ellipsis">{eventTimestamp}</div>} />;
      }
    }),
    columnHelper.accessor(row => row, {
      id: "created_at",
      header: t("v8_created_at", "Created At"),
      cell: ({ row: { original } }) => {
        const createdOn = formatDate(toDate(original.created_on), DATE_FORMATS_FNS.yearMonthDatehoursMinutes);
        return <Popup content={createdOn} trigger={<div className="sharebox-table-ellipsis">{createdOn}</div>} />;
      }
    })
  ];

  return (
    <div className="ShareboxDashboardTable">
      <ReactTable
        isFetching={isFetchingShareboxLogs}
        pagination={tablePagination}
        pageCount={nbPages}
        pageSize={PAGE_SIZE.PageSize100}
        totalItems={shareboxLogs.length * nbPages}
        onPaginationChange={handlePaginationChange}
        columns={columns}
        data={shareboxLogs}
      />
    </div>
  );
};
